import type { AppDispatch } from "src/datahandling/redux/store";
import { setConnectionId, setConnectionStatus, setError, handleReconnect, updateSensorData, setSubscribedSites } from "../store/slice";
import { WorkerCommand, WorkerMessageType, type WorkerMessage, type WorkerCommandMessage, type SignalRSensorData } from "../types";
import { MAIN_THREAD_DEBOUNCE_INTERVAL_MS } from "../constants";
import { addToGroups, removeFromGroups } from "../api/subscriptions";

export class SignalRManager {
	private static instance: SignalRManager | null = null;

	private worker: Worker | null = null;
	private dispatch: AppDispatch | null = null;

	private sensorDataQueue: SignalRSensorData[] = [];
	private debounceTimeout: ReturnType<typeof setTimeout> | null = null;
	private lastPushTime: number = Date.now();

	private currentConnectionId: string | null = null;
	private subscribedSiteIds: number[] = [];
	private isSubscribing: boolean = false;
	private pendingSiteIds: number[] | null = null;
	private isReconnecting: boolean = false;

	private constructor() {}

	public static getInstance(): SignalRManager {
		if (!SignalRManager.instance) {
			SignalRManager.instance = new SignalRManager();
		}
		return SignalRManager.instance;
	}

	public initialize(dispatch: AppDispatch): void {
		if (this.worker) {
			console.warn("[SignalRManager] Worker already initialized");
			return;
		}

		this.dispatch = dispatch;

		this.worker = new Worker(new URL("../worker/signalr.worker.ts", import.meta.url), { type: "module" });

		this.worker.addEventListener("message", this.handleWorkerMessage.bind(this));

		this.worker.addEventListener("error", (error) => {
			console.error("[SignalRManager] Worker error:", error);
			this.dispatch?.(
				setError({
					message: "Worker error occurred",
					timestamp: new Date().toISOString(),
					details: error,
				}),
			);
		});

		this.dispatch(setConnectionStatus("disconnected"));
	}

	public sendCommand(command: WorkerCommandMessage): void {
		if (!this.worker) {
			console.error("[SignalRManager] Cannot send command: worker not initialized");
			return;
		}
		this.worker.postMessage(command);
	}

	public async connect(signalRUrl: string, accessToken: string): Promise<void> {
		if (!this.worker || !this.dispatch) {
			throw new Error("Manager not initialized");
		}

		this.dispatch(setConnectionStatus("connecting"));

		this.sendCommand({
			command: WorkerCommand.SetToken,
			token: accessToken,
		});

		this.sendCommand({
			command: WorkerCommand.Initialize,
			signalRUrl,
		});
	}

	public updateToken(token: string): void {
		this.sendCommand({
			command: WorkerCommand.SetToken,
			token,
		});
	}

	public stop(): void {
		this.sendCommand({
			command: WorkerCommand.Stop,
		});
		this.dispatch?.(setConnectionStatus("disconnected"));
	}

	public terminate(): void {
		if (this.debounceTimeout) {
			clearTimeout(this.debounceTimeout);
		}

		this.sendCommand({
			command: WorkerCommand.Terminate,
		});

		this.worker = null;
		this.dispatch = null;
		this.currentConnectionId = null;
		this.subscribedSiteIds = [];
		this.pendingSiteIds = null;
		SignalRManager.instance = null;
	}

	public async subscribeToSites(siteIds: number[]): Promise<void> {
		if (!this.currentConnectionId) {
			return;
		}

		if (siteIds.length === 0) {
			return;
		}

		const siteIdsStr = JSON.stringify([...siteIds].sort());
		const subscribedSiteIdsStr = JSON.stringify([...this.subscribedSiteIds].sort());

		if (siteIdsStr === subscribedSiteIdsStr && !this.isSubscribing) {
			return;
		}

		if (this.isSubscribing) {
			this.pendingSiteIds = siteIds;
			return;
		}

		this.isSubscribing = true;

		const previousSiteIds = [...this.subscribedSiteIds];

		try {
			const newGroupNames = siteIds.map((id) => `SensorData_Site_${id}`);
			const success = await addToGroups(newGroupNames, this.currentConnectionId);

			if (!success) {
				throw new Error("Failed to subscribe to site groups");
			}

			if (previousSiteIds.length > 0) {
				const oldGroupNames = previousSiteIds.map((id) => `SensorData_Site_${id}`);
				await removeFromGroups(oldGroupNames, this.currentConnectionId);
			}

			this.subscribedSiteIds = siteIds;
			const sitesHash = siteIds.length > 0 ? JSON.stringify([...siteIds].sort()) : null;
			this.dispatch?.(setSubscribedSites(sitesHash));
		} catch (error) {
			console.error("[SignalRManager] Subscription failed:", error);

			if (previousSiteIds.length > 0) {
				const restoreGroupNames = previousSiteIds.map((id) => `SensorData_Site_${id}`);
				await addToGroups(restoreGroupNames, this.currentConnectionId);
			}

			this.dispatch?.(
				setError({
					message: "Failed to subscribe to site data",
					timestamp: new Date().toISOString(),
					details: error,
				}),
			);
		} finally {
			this.isSubscribing = false;

			if (this.pendingSiteIds) {
				const nextSiteIds = this.pendingSiteIds;
				this.pendingSiteIds = null;
				this.subscribeToSites(nextSiteIds);
			}
		}
	}

	public async unsubscribeFromSites(siteIds: number[]): Promise<void> {
		if (!this.currentConnectionId || siteIds.length === 0) {
			return;
		}

		try {
			const groupNames = siteIds.map((id) => `SensorData_Site_${id}`);
			await removeFromGroups(groupNames, this.currentConnectionId);
			
			this.subscribedSiteIds = this.subscribedSiteIds.filter(
				(id) => !siteIds.includes(id)
			);
			
			const sitesHash = this.subscribedSiteIds.length > 0 
				? JSON.stringify([...this.subscribedSiteIds].sort()) 
				: null;
			this.dispatch?.(setSubscribedSites(sitesHash));
		} catch (error) {
			console.error("[SignalRManager] Error during unsubscription:", error);
		}
	}

	private flushSensorDataQueue(): void {
		if (this.sensorDataQueue.length === 0) return;
		if (!this.dispatch) return;

		const sortedData = [...this.sensorDataQueue].sort((a, b) => a.dateTime.localeCompare(b.dateTime));

		this.dispatch(updateSensorData(sortedData));

		this.sensorDataQueue = [];
		this.lastPushTime = Date.now();

		if (this.debounceTimeout) {
			clearTimeout(this.debounceTimeout);
			this.debounceTimeout = null;
		}
	}

	private enqueueSensorData(data: SignalRSensorData[]): void {
		this.sensorDataQueue.push(...data);

		const now = Date.now();
		const timeSinceLastPush = now - this.lastPushTime;

		if (timeSinceLastPush >= MAIN_THREAD_DEBOUNCE_INTERVAL_MS) {
			this.flushSensorDataQueue();
			return;
		}

		if (!this.debounceTimeout) {
			const remainingTime = MAIN_THREAD_DEBOUNCE_INTERVAL_MS - timeSinceLastPush;
			this.debounceTimeout = setTimeout(() => this.flushSensorDataQueue(), remainingTime);
		}
	}

	private handleWorkerMessage(event: MessageEvent<WorkerMessage>): void {
		if (!this.dispatch) return;

		const message = event.data;

		switch (message.type) {
			case WorkerMessageType.ConnectionId: {
				const { connectionId } = message.payload;
				this.currentConnectionId = connectionId;
				this.dispatch(setConnectionId(connectionId));

				if (this.isReconnecting && this.subscribedSiteIds.length > 0) {
					const sitesToResubscribe = [...this.subscribedSiteIds];
					this.subscribedSiteIds = [];
					this.subscribeToSites(sitesToResubscribe);
					this.isReconnecting = false;
				}
				break;
			}

			case WorkerMessageType.Error: {
				const { error, details } = message.payload;
				console.error("[SignalRManager] Worker error:", error, details);
				this.dispatch(
					setError({
						message: error,
						timestamp: new Date().toISOString(),
						details,
					}),
				);
				break;
			}

			case WorkerMessageType.Reconnect: {
				this.dispatch(handleReconnect());
				this.isReconnecting = true;
				break;
			}

			case WorkerMessageType.SensorData: {
				this.enqueueSensorData(message.payload);
				break;
			}

			case WorkerMessageType.Pong: {
				break;
			}

			default: {
				console.warn("[SignalRManager] Unknown message type:", message);
			}
		}
	}
}
